/**
 * Панель привязки MAX (VK): статус, генерация ссылки
 * Дата: 10.01.2026 - Создан для TODO-028a (MAX Linking)
 * Дата: 23.02.2026 - Убран ChannelSelector и кнопка "Отправить" — коллеги копируют ссылку
 *   и вставляют в нужный мессенджер сами (по аналогии с TG)
 * Связан с: frontend/src/components/features/messages/ChatWindow.tsx
 * Компонент: UI компонент для управления привязкой MAX
 */
import { useState } from 'react';
import { Button } from '@/components/ui';
import { useGenerateLink, useRequestContact } from '@/hooks/useLinking';
import { useToast } from '@/hooks/useToast';
import { CheckCircle2, AlertCircle, Copy, UserPlus } from 'lucide-react';

interface MaxLinkPanelProps {
  phoneNumber: string;
  isLinked: boolean;
  maxUsername?: string;
  availableChannels?: string[]; // Оставлено для обратной совместимости (не используется)
}

export function MaxLinkPanel({ 
  phoneNumber, 
  isLinked, 
  maxUsername,
}: MaxLinkPanelProps) {
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [linkGenerated, setLinkGenerated] = useState(false);
  const [shortCode, setShortCode] = useState<string | null>(null);
  
  const generateLinkMutation = useGenerateLink();
  const requestContactMutation = useRequestContact();
  const { showSuccess, showError } = useToast();
  
  // Определяем известен ли телефон
  const isPhoneKnown = /^7\d{10}$/.test(phoneNumber);
  const isMaxId = phoneNumber.startsWith('max:') || (!isPhoneKnown && /^\d+$/.test(phoneNumber));
  
  // Дата: 10.01.2026 - Определяем messenger_id для reverse linking
  const getMessengerId = (): string | null => {
    if (!isMaxId || isPhoneKnown) return null;
    
    // Извлекаем messenger_id из phoneNumber
    return phoneNumber.startsWith('max:') 
      ? phoneNumber.replace('max:', '') 
      : phoneNumber;
  };
  
  // Если уже привязан
  if (isLinked) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2 text-green-700">
        <CheckCircle2 className="w-5 h-5" />
        <span className="text-sm font-medium">
          MAX привязан{maxUsername && `: @${maxUsername}`}
        </span>
      </div>
    );
  }
  
  // Если телефон неизвестен (только MAX ID) — показать UI для reverse linking
  // TODO: TODO-012b — реализовать reverse linking с кнопкой "Запросить контакт в MAX"
  if (isMaxId && !isPhoneKnown) {
    return (
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 space-y-2">
        <div className="flex items-center gap-2 text-purple-700">
          <AlertCircle className="w-5 h-5" />
          <span className="text-sm font-medium">
            Есть MAX. Остальные мессенджеры не привязаны.
          </span>
        </div>
        <p className="text-xs text-purple-600">
          Телефон пациента неизвестен. Для привязки запросите контакт у пациента.
        </p>
        {/* Дата: 10.01.2026 - TODO-012b: Кнопка "Запросить контакт в MAX" */}
        <Button 
          variant="secondary"
          size="sm"
          disabled={requestContactMutation.isPending}
          onClick={async () => {
            const messengerId = getMessengerId();
            if (!messengerId) {
              showError('Не удалось определить идентификатор MAX');
              return;
            }
            
            // Показать confirmation dialog
            const confirmed = window.confirm(
              'Отправить пациенту запрос на подтверждение номера телефона через MAX?'
            );
            
            if (!confirmed) return;
            
            requestContactMutation.mutate(
              { messenger_id: messengerId, channel: 'max' },
              {
                onSuccess: () => {
                  showSuccess('Запрос контакта отправлен пациенту');
                },
                onError: (error: Error) => {
                  showError(`Ошибка отправки запроса: ${error.message}`);
                },
              }
            );
          }}
          testId="btn-request-contact-max"
        >
          <UserPlus className="w-4 h-4 mr-1" />
          {requestContactMutation.isPending ? 'Отправка...' : 'Запросить контакт в MAX'}
        </Button>
      </div>
    );
  }
  
  const handleGenerateLink = async () => {
    try {
      const resp = await generateLinkMutation.mutateAsync({ phone: phoneNumber, messenger: 'max' });
      setGeneratedLink(resp.deep_link);
      // Короткий код — первые 8 символов токена (для ввода в бот, если deep link не работает)
      if (resp.token) {
        setShortCode(resp.token.substring(0, 8));
      }
      setLinkGenerated(true);
      showSuccess('Ссылка для привязки MAX создана! Скопируйте и отправьте пациенту.');
    } catch (error) {
      showError('Ошибка генерации ссылки MAX');
    }
  };
  
  // Копирование ссылки
  const handleCopyLink = async () => {
    if (generatedLink) {
      await navigator.clipboard.writeText(generatedLink);
      showSuccess('Ссылка скопирована в буфер обмена');
    }
  };
  
  return (
    <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 space-y-3">
      {/* Статус */}
      <div className="flex items-center gap-2 text-purple-700">
        <AlertCircle className="w-5 h-5" />
        <span className="text-sm font-medium">MAX не привязан</span>
      </div>
      
      {/* Генерация ссылки */}
      {!linkGenerated && (
        <Button 
          onClick={handleGenerateLink}
          variant="secondary"
          size="sm"
          disabled={generateLinkMutation.isPending}
          testId="btn-generate-max-link"
        >
          {generateLinkMutation.isPending ? 'Генерация…' : 'Сгенерировать ссылку MAX'}
        </Button>
      )}
      
      {/* Сгенерированная ссылка — копировать и отправить вручную */}
      {linkGenerated && generatedLink && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={generatedLink}
              readOnly
              className="input flex-1 text-xs"
              data-testid="input-generated-max-link"
            />
            <Button
              onClick={handleCopyLink}
              variant="ghost"
              size="sm"
              testId="btn-copy-max-link"
            >
              <Copy className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-xs text-purple-600">
            Скопируйте ссылку и отправьте пациенту в любой мессенджер
          </p>
          
          {/* Короткий код — альтернатива deep link для уже запущенных ботов */}
          {shortCode && (
            <div className="bg-white border border-purple-200 rounded-md p-2 mt-1">
              <p className="text-xs text-purple-700 font-medium mb-1">
                Если бот уже открыт — пациент может ввести код:
              </p>
              <div className="flex items-center gap-2">
                <code className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-sm font-mono font-bold tracking-wider">
                  {shortCode}
                </code>
                <Button
                  onClick={async () => {
                    if (shortCode) {
                      await navigator.clipboard.writeText(shortCode);
                      showSuccess('Код скопирован');
                    }
                  }}
                  variant="ghost"
                  size="sm"
                  testId="btn-copy-max-code"
                >
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

